import { tap } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { Service } from "./base-service";
import { SocketService } from "./socket.service";
import { PriceAlertNotifyService } from "./notify/price-alert-notify.service";
import { Helper } from "@helpers/functions";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";

import { PriceAlert } from "@models";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { DestructorService } from "@core-services/destructor.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

const name = ServiceNames.PRICE_ALERT;

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class PriceAlertService extends Service<PriceAlert> {
	public name: string = ServiceNames.PRICE_ALERT;

	public constructor(
		private apicallService: ApicallService,
		private infoService: InfoService,
		private socketService: SocketService,
		protected translationService: TranslationService,
		private priceAlertNotifyService: PriceAlertNotifyService,
		protected initService: InitService,
		protected destructor: DestructorService
	) {
		super(initService, destructor, name);
	}

	public initAlerts(): void {
		this.list = [];
		// Subscribe to socket event in order to display desktop notification
		this.socketService
			.joinRoom<PriceAlert>({
				name: "price_alerts",
				type: PriceAlert,
				isPrivate: true
			})
			.pipe(untilDestroyed(this))
			.subscribe((priceAlert) => {
				this.priceAlertNotifyService.show("trigger", priceAlert);
			});
	}

	public getPriceAlerts(): void {
		const a = new ApicallOption(PriceAlert);
		this.apicallService.get("pricealerts", a).subscribe({
			next: (priceAlerts: PriceAlert[]) => {
				this.list = priceAlerts.sort((p1, p2) => p2.getAmount() - p1.getAmount());
				this.initService.init(this.name);
			},
			error: (error) => {
				Helper.handleError(error);
				this.list = []; // In case the pricealerts call fails, we still init with an empty list so that we don't block the whole app.
				this.initService.init(this.name);
			}
		});
	}

	public add(priceAlert: PriceAlert): Observable<PriceAlert[]> {
		const apiOptions = new ApicallOption(PriceAlert, priceAlert);
		apiOptions.apiVersion = "v2";
		return this.apicallService.post("pricealerts", apiOptions).pipe(
			tap({
				next: (priceAlerts: PriceAlert[]) => {
					priceAlerts.sort((p1, p2) => p2.getAmount() - p1.getAmount());
					this.addItems(priceAlerts);
					this.infoService.setSuccess(this.translationService.translate("info_text_pricealert_saved"));
				},
				error: (err) => {
					Helper.handleError(err, priceAlert, {
						forceToast: true
					});
				}
			})
		);
	}

	public deleteAlert(alert: PriceAlert): void {
		this.apicallService.deleteRequest(`pricealert/${alert.getId()}`).subscribe({
			next: () => {
				this.deleteItem(alert);
				this.infoService.setSuccess(this.translationService.translate("info_text_pricealert_deleted"));
			},
			error: (error) => {
				console.error(error);
			}
		});
	}
}
